import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ServiceCard } from '../components/ServiceCard';
import { Phone, Shield, Clock, DollarSign, Star, MapPin } from 'lucide-react';
import { supabase } from '../lib/supabase';

export function ServicesPage() {
  const [services, setServices] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    async function loadServices() {
      const { data } = await supabase
        .from('services')
        .select('*')
        .order('title');

      if (data) {
        setServices(data);
      }
      setLoading(false);
    }

    loadServices();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading services...</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Professional Plumbing Services | Emergency & Residential - NearPlumbers</title>
        <meta 
          name="description" 
          content="24/7 professional plumbing services including emergency repairs, drain cleaning, water heater installation & maintenance. Licensed & insured plumbers, fair pricing." 
        />
        <meta name="keywords" content="plumbing services, emergency plumber, drain cleaning, water heater repair, plumbing installation, licensed plumber" />
        <link rel="canonical" href="https://nearplumbers.com/services" />
      </Helmet>

      <div className="bg-white">
        <div className="relative py-24 bg-gradient-to-r from-blue-700 to-blue-900">
          <div className="absolute inset-0">
            <img
              className="w-full h-full object-cover opacity-20"
              src="https://images.unsplash.com/photo-1585704032915-c3400ca199e7?auto=format&fit=crop&q=80"
              alt="Plumbing Services"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black opacity-40"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white mb-4">
                Expert Plumbing Services
                <span className="block text-2xl mt-2 text-blue-200">Available 24/7 for Emergencies</span>
              </h1>
              <p className="text-xl text-gray-100 max-w-3xl mx-auto">
                Licensed & insured plumbers providing fast, reliable solutions for all your residential and commercial needs
              </p>
              <div className="mt-8 flex justify-center">
                <a
                  href="tel:(855) 513-0871"
                  className="inline-flex items-center px-8 py-4 bg-white text-blue-600 rounded-full text-lg font-semibold hover:bg-blue-50 transition-colors"
                >
                  <Phone className="w-6 h-6 mr-2" />
                  Call Now: (855) 513-0871
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto py-24 px-4 sm:px-6 lg:px-8">
          {/* Service Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <Clock className="w-10 h-10 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">24/7 Availability</h3>
              <p className="text-gray-600">Emergency service available around the clock</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <Shield className="w-10 h-10 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Licensed & Insured</h3>
              <p className="text-gray-600">Fully certified professional plumbers</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <DollarSign className="w-10 h-10 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Upfront Pricing</h3>
              <p className="text-gray-600">No hidden fees or surprise charges</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <Star className="w-10 h-10 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Satisfaction Guaranteed</h3>
              <p className="text-gray-600">100% satisfaction on all services</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Our Professional Plumbing Services
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            From emergency repairs to routine maintenance, our expert plumbers have you covered
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Link 
                key={service.id} 
                to={`/services/${service.slug}`}
                className="block"
              >
                <ServiceCard service={service} />
              </Link>
            ))}
          </div>
        </div>

        {/* Service Areas Section */}
        <div className="bg-gray-50 py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Service Areas</h2>
              <p className="text-xl text-gray-600">Trusted plumbing services across major cities</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <Link to="/ny/new-york" className="group">
                <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                  <MapPin className="w-8 h-8 text-blue-600 mb-3" />
                  <h3 className="font-semibold group-hover:text-blue-600">New York, NY</h3>
                </div>
              </Link>
              <Link to="/ca/los-angeles" className="group">
                <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                  <MapPin className="w-8 h-8 text-blue-600 mb-3" />
                  <h3 className="font-semibold group-hover:text-blue-600">Los Angeles, CA</h3>
                </div>
              </Link>
              <Link to="/il/chicago" className="group">
                <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                  <MapPin className="w-8 h-8 text-blue-600 mb-3" />
                  <h3 className="font-semibold group-hover:text-blue-600">Chicago, IL</h3>
                </div>
              </Link>
              <Link to="/tx/houston" className="group">
                <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                  <MapPin className="w-8 h-8 text-blue-600 mb-3" />
                  <h3 className="font-semibold group-hover:text-blue-600">Houston, TX</h3>
                </div>
              </Link>
            </div>
            <div className="text-center mt-8">
              <Link
                to="/locations"
                className="inline-flex items-center text-blue-600 hover:text-blue-700"
              >
                View All Service Areas
                <svg className="w-5 h-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto py-24 px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">Do you offer emergency plumbing services?</h3>
              <p className="text-gray-600">Yes, we provide 24/7 emergency plumbing services. Our licensed plumbers are available around the clock to handle urgent issues like burst pipes, severe leaks, and sewage backups.</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Are your plumbers licensed and insured?</h3>
              <p className="text-gray-600">All our plumbers are fully licensed, insured, and background-checked. We maintain strict quality standards to ensure you receive professional and reliable service.</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What areas do you serve?</h3>
              <p className="text-gray-600">We provide plumbing services across major cities nationwide. Enter your ZIP code to find licensed plumbers in your area.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}